import React, { useContext, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, Form, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import AppContext from 'context/Context';
import { patch, post } from 'services/salesperson-kpi/kpiTargetService';
import SpAccountManagerDropdown from 'components/dropdowns/SpSalePerson/SpAccountManagerDropdown';
import InputDatePicker from 'components/common/InputDatePicker';
import moment from 'moment';

const KpiTargetModal = ({ isShow, data, handleClose, handleSubmit }) => {
  const {
    config: { isDark }
  } = useContext(AppContext);

  const [formData, setFormData] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    const parsed = {
      ...data,
      applyFromInput: moment(data?.applyFrom).toDate()
    };
    setFormData(parsed);
  }, [isShow, data]);

  const onSubmit = async e => {
    e.preventDefault();
    setIsSubmitted(true);
    const form = formRef.current;
    if (!form.checkValidity() || hasDropdownError()) {
      e.stopPropagation();
      form.classList.add('was-validated');
      return;
    }

    const payload = {
      ...formData,
      applyFrom: moment(formData.applyFromInput).format('YYYY-MM-DD'),
      prospect: parseInt(formData.prospect, 10),
      existing: parseInt(formData.existing, 10),
      openAccount: parseInt(formData.openAccount, 10)
    };

    if (formData?.id > 0) {
      await patch(formData.id, payload);
    } else {
      await post(payload);
    }
    handleSubmit();
  };

  const handleSaleChange = (key, value) => {
    setFormData({ ...formData, [key]: value.id });
  };

  const handleChange = ({ target }) => {
    const { name, value } = target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDateChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

  const hasDropdownError = () => {
    return (
      formData?.saleUserId < 0 ||
      formData?.saleUserId == undefined ||
      formData?.saleUserId == null
    );
  };

  return (
    <Modal
      show={isShow}
      onHide={(e) => {
        handleClose(e);
        setIsSubmitted(false);
      }}
      contentClassName="border"
    >
      <Form ref={formRef} noValidate onSubmit={onSubmit}>
        <Modal.Header
          closeButton
          closeVariant={isDark ? 'white' : undefined}
          className="bg-light px-card border-bottom-0"
        >
          <Modal.Title as="h5">
            {formData?.id > 0 ? 'Edit' : 'New'} Target
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-card">
          <Form.Group className="mb-3">
            <Form.Label className="fs-0 required">Apply From</Form.Label>
            <InputDatePicker
              column="applyFromInput"
              className="mb-3"
              selected={formData?.applyFromInput}
              handleChange={handleDateChange}
              required
              showError={isSubmitted}
            />
            <div className="invalid-feedback">Please select a date</div>
          </Form.Group>

          <Form.Group className="mb-3" controlId="targetWeek">
            <Form.Label className="fs-0 required">Account Manager</Form.Label>
            <SpAccountManagerDropdown
              fieldName="saleUserId"
              handleChange={handleSaleChange}
              selectedOptions={formData?.saleUserId}
              hasError={hasDropdownError}
              required
              showError={isSubmitted}
            />
            <div className="invalid-feedback">Please select a salesperson</div>
          </Form.Group>

          <Form.Group className="mb-3" controlId="prospect">
            <Form.Label className="fs-0 required">Prospect</Form.Label>
            <Form.Control
              type="number"
              name="prospect"
              required
              value={formData?.prospect ?? ''}
              onChange={handleChange}
              min="1"
            />
            <div className="invalid-feedback">
              Prospect must be greater than 0
            </div>
          </Form.Group>

          <Form.Group className="mb-3" controlId="existing">
            <Form.Label className="fs-0 required">Existing</Form.Label>
            <Form.Control
              type="number"
              name="existing"
              required
              value={formData?.existing ?? ''}
              onChange={handleChange}
              min="1"
            />
            <div className="invalid-feedback">
              Existing must be greater than 0
            </div>
          </Form.Group>

          {/* <Form.Group className="mb-3" controlId="openAccount">
            <Form.Label className="fs-0 required">Open Account</Form.Label>
            <Form.Control
              type="number"
              name="openAccount"
              required
              value={formData?.openAccount ?? ''}
              onChange={handleChange}
              min="1"
            />
            <div className="invalid-feedback">
              Open Account must be greater than 0
            </div>
          </Form.Group> */}
        </Modal.Body>

        <Modal.Footer className="bg-light px-card border-top-0">
          <Link to="#!" className="me-3 text-600">
            More options
          </Link>
          <Button variant="primary" type="submit" className="px-4 mx-0">
            Save
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

KpiTargetModal.propTypes = {
  id: PropTypes.number,
  isShow: PropTypes.bool,
  handleClose: PropTypes.func,
  handleSubmit: PropTypes.func,
  data: PropTypes.shape({
    id: PropTypes.number,
    year: PropTypes.number,
    weekNumber: PropTypes.number,
    prospect: PropTypes.number,
    existing: PropTypes.number,
    openAccount: PropTypes.number,
    applyFrom: PropTypes.string,
  }),
  name: PropTypes.string
};

export default KpiTargetModal;
